import type { FC } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import { cn } from '@/utils';
import { INTRO_HEART_ITEMS } from '../constants';
import HeartEmptyIcon from './HeartEmptyIcon';
import HeartFilledIcon from './HeartFilledIcon';

type CreatorChatFlowIntroStatusProps = {
	completedHearts: number;
};

const CreatorChatFlowIntroStatus: FC<CreatorChatFlowIntroStatusProps> = ({
	completedHearts
}) => {
	const { localization } = useHostContext();

	return (
		<div
			className="flex flex-col items-center gap-5 rounded-[40px] px-10 py-8 text-center backdrop-blur-[14px]"
			data-testid="creator-chat-flow-intro-status"
			style={{ background: 'rgba(244, 247, 250, 0.53)' }}
		>
			<p className="max-w-[320px] text-[52px] font-semibold leading-[0.92] text-black [font-family:var(--font-headlines)]">
				<span className="block whitespace-nowrap">
					{localization.translate('creator', 'chatFlow.intro.titleLineOne')}
				</span>
				<span className="block whitespace-nowrap">
					{localization.translate('creator', 'chatFlow.intro.titleLineTwo')}
				</span>
			</p>

			<div
				aria-label={localization.translate(
					'creator',
					'chatFlow.intro.progressLabel'
				)}
				className="flex items-center gap-2"
				data-testid="creator-chat-flow-heart-progress"
				role="progressbar"
			>
				{INTRO_HEART_ITEMS.map(({ containerSize, heartSize, id, index }) => {
					const isFilled = index < completedHearts;
					const isActive = isFilled && index === completedHearts - 1;

					return (
						<div
							className="relative flex items-center justify-center"
							data-state={isFilled ? 'filled' : 'empty'}
							data-testid={`creator-chat-flow-heart-${index}`}
							key={id}
							style={{
								height: `${containerSize}px`,
								width: `${containerSize}px`
							}}
						>
							{isFilled ? (
								<HeartFilledIcon
									className={cn(
										'relative z-10 text-primary-900 transition-transform duration-300',
										isActive && 'scale-110'
									)}
									style={{
										height: `${heartSize}px`,
										width: `${heartSize}px`
									}}
								/>
							) : (
								<HeartEmptyIcon
									className="relative z-10 text-primary-900/55"
									style={{
										height: `${heartSize}px`,
										width: `${heartSize}px`
									}}
								/>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default CreatorChatFlowIntroStatus;
